import React from 'react';
import { Alert, Box, Button, IconButton, Tooltip } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';

import ScreenLayout from '@giro-pdv/layouts/Screen.layout';

import useFetchHook from '@giro-pdv/hooks/useFetch.hook';

import getApiTerminalsRegistrationsService from '@giro/shared-services/pdv/getApiTerminalsRegistrations.service';

import { isActiveStatus } from '../terminals.types';

import TableTerminalsComponent from '../components/TableTerminals.component';
import TableTerminalRegistrationsComponent from '../components/TableTerminalRegistrations.component';
import DialogShowQrComponent from '../components/DialogShowQr.component';
import DialogCreateTerminalComponent from '../components/DialogCreateTerminal.component';
import DialogRenameTerminalComponent from '../components/DialogRenameTerminal.component';
import DialogDeleteTerminalComponent from '../components/DialogDeleteTerminal.component';

const TerminalsScreen = () => {
  const listFetch = useFetchHook(
    () => getApiTerminalsRegistrationsService({ type: 'gtapp' }),
    { requestOnMount: true }
  );

  const rows = Array.isArray(listFetch.data) ? listFetch.data : [];
  // Deleted (canceled) terminals stay in the backend as a terminal record but must not show
  // in either section — hide them from the operator's lists.
  const visible = rows.filter((r) => r?.status !== 'canceled');
  const active = visible.filter((r) => isActiveStatus(r?.status));
  const pending = visible.filter((r) => !isActiveStatus(r?.status));

  const reload = () => listFetch.handleFetch();

  const [showCreate, setShowCreate] = React.useState(false);
  const [qrTarget, setQrTarget] = React.useState<any>(null);
  const [renameTarget, setRenameTarget] = React.useState<any>(null);
  const [deleteTarget, setDeleteTarget] = React.useState<any>(null);

  const handleCreated = (created) => {
    setShowCreate(false);
    if (created) {
      setQrTarget({
        registration: { id: created.id },
        initialReveal: {
          id: created.id,
          otp: created.otp,
          otp_expires_at: created.otp_expires_at,
        },
      });
    }
    reload();
  };

  const cta = (
    <Box display="flex" alignItems="center" gap={1}>
      <Tooltip title="Atualizar">
        <IconButton onClick={reload} size="small" disabled={listFetch.loading}>
          <RefreshIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Button
        variant="contained"
        startIcon={<AddIcon />}
        onClick={() => setShowCreate(true)}
      >
        Adicionar dispositivo
      </Button>
    </Box>
  );

  return (
    <ScreenLayout title="Tap on Phone" cta={cta}>
      <Box display="flex" flexDirection="column" gap={8}>
        {listFetch.error && (
          <Alert severity="error">
            Não foi possível atualizar os dispositivos. Tente novamente em
            Atualizar.
          </Alert>
        )}
        {!listFetch.error && (
          <>
            <TableTerminalsComponent
              rows={active}
              loading={listFetch.loading}
              onRename={setRenameTarget}
              onReauth={(row) =>
                setQrTarget({ registration: { id: row.id }, reauth: true })
              }
              onDelete={setDeleteTarget}
            />
            <TableTerminalRegistrationsComponent
              rows={pending}
              loading={listFetch.loading}
              onShowQr={setQrTarget}
              onRename={setRenameTarget}
              onDelete={setDeleteTarget}
            />
          </>
        )}
      </Box>

      <DialogCreateTerminalComponent
        open={showCreate}
        onClose={() => setShowCreate(false)}
        onCreated={handleCreated}
      />

      <DialogShowQrComponent
        open={Boolean(qrTarget)}
        registration={qrTarget?.registration || qrTarget || null}
        initialReveal={qrTarget?.initialReveal || null}
        reauth={Boolean(qrTarget?.reauth)}
        onClose={() => setQrTarget(null)}
      />

      <DialogRenameTerminalComponent
        open={Boolean(renameTarget)}
        registration={renameTarget}
        onClose={() => setRenameTarget(null)}
        onRenamed={reload}
      />

      <DialogDeleteTerminalComponent
        open={Boolean(deleteTarget)}
        registration={deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onDeleted={reload}
      />
    </ScreenLayout>
  );
};

export default TerminalsScreen;
